import { appNoOp, onError } from '@actions/app.action';
import { Injectable } from '@angular/core';
import { BaseError, ErrorType, NullableString } from '@models/app.models';
import { Passenger, UnitInformation } from '@models/booking.models';
import { CountryPhoneCode } from '@models/cms/app.model';
import {
  PassengerForm,
  TripDetailsPassengerForm,
} from '@models/form/form.models';
import {
  PassengerUpdateRequest,
  PassengerAssistanceUpdateRequest,
} from '@models/requests/passenger-update-request.model';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  selectBookingPrimaryContact,
  selectPassengers,
  selectSegmentsWithJourneyKeys,
  selectOriginFullName,
} from '@selectors/booking.selector';
import { selectAvailabilityRedirectId, selectAvailabilitySelectedFaresState } from '@selectors/availability.selector';
import { selectPhoneCodes } from '@selectors/resource.selector';
import { selectLoggedInUserCustomerNumber } from '@selectors/user.selector';
import { ContactService } from '@services/api-clients/contact.service';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { PassengerService } from 'src/app/services/api-clients/passenger.service';
import {
  autoAssignSeats,
  updatePassengers,
  updatePassengersAssistance,
  updatePassengersAssistanceSuccess,
  updatePassengersSuccess,
  updatePrimaryContactSuccess,
  updateTripDetailsPassengers,
  updateTripsPassengersSuccess,
} from '../actions/passenger.action';
import { paymentSubmitPayments } from '@actions/payment.action';
import { selectMarketingOptIn } from '@selectors/payment.selector';
import {
  CULTURE_ROUTE_PARAM,
  CultureKeyType,
  ECONOMY_CABIN_DESIGNATOR,
  PREMIUM_CABIN_DESIGNATOR,
  FALLBACK_CULTURE
} from '@constants/app.constants';
import { selectRouteParam, selectUrl } from '@selectors/router.selector';
import { CabinProductClass } from "@constants/cabin.constants";
import { SeatService } from "@services/api-clients/seat.service";
import { selectSeatMaps } from "@selectors/seat.selector";
import {
  findGoodSeat,
  findGoodSeatsForXPassengers, isFlextra
} from "@utils/seats-utils";
import { N0PassengerSeatRequest } from "@models/requests/passenger-seat-request";
import { updatePassengerSeats } from "@actions/seat.action";
import { bagsSellBagSsrs } from "@actions/bags.action";
import { extrasSellExtrasSsrs } from "@actions/extras.action";
import { mealsSellSelectedMeals } from "@actions/meals.action";
import { selectCurrentCultureCode } from "@selectors/culture.selector";
import { CheckInService } from '@services/api-clients/check-in.service';
import {ScrollService} from "@services/scroll/scroll.service";
import { getRootPath } from '@utils/data-utils';
@Injectable()
export class PassengerEffect {
  constructor(
    private actions$: Actions,
    private passengerService: PassengerService,
    private contactService: ContactService,
    private checkInService: CheckInService,
    // seatService needed for feat-002191-skip-to-payment-button only
    private seatService: SeatService,
    private scrollService: ScrollService,
    private store: Store,
  ) {}

  updatePassengers$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(updatePassengers),
      concatLatestFrom(() => [
        this.store.select(selectPassengers),
        this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM)) as Observable<
          CultureKeyType | undefined
        >,
        this.store.select(selectLoggedInUserCustomerNumber),
        this.store.select(selectBookingPrimaryContact),
        this.store.select(selectPhoneCodes),
        this.store.select(selectAvailabilityRedirectId),
        this.store.select(selectUrl),
      ]),
      mergeMap(
        ([
          { passengers: passengerForms, otherPassengerPrimaryContact, redirectToPayment },
          passengers,
          cultureCode,
          customerNumber,
          primaryContact,
          phoneCodes,
          redirectId,
          url,
        ]) => {
          const paxFormsArr = Object.values(passengerForms);
          if (
            !passengers ||
            paxFormsArr.length <= 0 ||
            !cultureCode ||
            !phoneCodes
          ) {
            return of(
              onError({
                error: new BaseError(ErrorType.PassengersUpdateFailure),
              }),
            );
          }
          const passengersUpdateRequests = this.getPassengerUpdates(
            passengers,
            phoneCodes,
            passengerForms,
            cultureCode,
          );

          const passengerAssistanceUpdateRequests =
            this.getPassengerAssistanceUpdates(passengers, passengerForms);

          const apiRequests = [
            this.passengerService.updatePassengersInformation(
              passengersUpdateRequests,
            ),
            this.passengerService.updatePassengersAssistance(
              passengerAssistanceUpdateRequests,
            ),
          ];

          // TODO: does this preserve order? Or do we need to correlate
          // the passenger key with their order in the forms map?

          // we know this is defined and contains relevant data
          // as getPassengerUpdates would have thrown an error otherwise.
          const passengerOneForm = paxFormsArr[0];

          const contactRequest =
            passengerOneForm &&
            this.contactService.transformFormDataToContactRequest(
              otherPassengerPrimaryContact
                ? otherPassengerPrimaryContact
                : passengerOneForm,
              phoneCodes,
              cultureCode,
              customerNumber,
            );

          // If redirectId exists then populate it in primary contact's address - lineThree field.
          if (redirectId && contactRequest) {
            if (contactRequest.address) {
              contactRequest.address = {
                ...contactRequest.address,
                lineThree: redirectId,
              };
            } else {
              contactRequest.address = {
                lineThree: redirectId,
              };
            }
          }

          const isPrimaryContactAlreadyAdded = !!primaryContact;
          if (contactRequest) {
            apiRequests.push(
              isPrimaryContactAlreadyAdded
                ? this.contactService.updatePrimaryContact(contactRequest)
                : this.contactService.addPrimaryContact(contactRequest),
            );
          }

          const flow = getRootPath(url);
          return forkJoin(apiRequests).pipe(
            map(() => updatePassengersSuccess({redirectToPayment, flow})),
            catchError(() =>
              of(
                onError({
                  error: new BaseError(ErrorType.PassengersUpdateFailure),
                }),
              ),
            ),
          );
        },
      ),
    );
  });

  updateTripDetailsPassengers$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateTripDetailsPassengers),
      concatLatestFrom(() => [
        this.store.select(selectPassengers),
        this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM)) as Observable<
          CultureKeyType | undefined
        >,
        this.store.select(selectPhoneCodes),
      ]),
      mergeMap(
        ([
          { passengers: passengerForms },
          passengers,
          cultureCode,
          phoneCodes,
        ]) => {
          if (!passengers || !cultureCode || !phoneCodes) {
            return of(
              onError({
                error: new BaseError(ErrorType.PassengersUpdateFailure),
              }),
            );
          }
          const passengersUpdateRequests = this.getTripDetailsPassengerUpdates(
            passengers,
            passengerForms,
            phoneCodes,
            cultureCode,
          );

          const passengerAssistanceUpdateRequests =
            this.getPassengerAssistanceUpdates(passengers, passengerForms);

          const apiRequests = [
            this.passengerService.updatePassengersInformation(
              passengersUpdateRequests,
            ),
            this.passengerService.updatePassengersAssistance(
              passengerAssistanceUpdateRequests,
            ),
          ];

          return forkJoin(apiRequests).pipe(
            switchMap(() => {
              return of(updateTripsPassengersSuccess())}
            ),
            catchError(() =>
              of(
                onError({
                  error: new BaseError(ErrorType.PassengersUpdateFailure),
                }),
              ),
            ),
          );
        },
      ),
    );
  });

  updatePassengersAssistance$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(updatePassengersAssistance),
      mergeMap((action) =>
        this.passengerService.updatePassengersAssistance(action.payload).pipe(
          map(() => updatePassengersAssistanceSuccess()),
          catchError(() =>
            of(
              onError({
                error: new BaseError(ErrorType.PassengersUpdateFailure),
              }),
            ),
          ),
        ),
      ),
    );
  });

  updateMarketingOptIn$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(paymentSubmitPayments),
      concatLatestFrom(() => [
        this.store.select(selectBookingPrimaryContact),
        this.store.select(selectMarketingOptIn),
        this.store.select(selectCurrentCultureCode),
        this.store.select(selectOriginFullName)
      ]),
      switchMap(([, primaryContact, enable, culture, originAirportCode]) => {
        if (!primaryContact) {
          return of(appNoOp());
        }
        return this.contactService
          .updatePrimaryContact({
            ...primaryContact,
            isMarketingOptIn: enable,
            locale: culture ?? FALLBACK_CULTURE,
            preferredAirport: originAirportCode
          })
          .pipe(map(() => updatePrimaryContactSuccess()));
      }),
    );
  });

  // needed for feat-002191-skip-to-payment-button only
  updatePassengersSuccess$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(updatePassengersSuccess, bagsSellBagSsrs, extrasSellExtrasSsrs, mealsSellSelectedMeals),
      concatLatestFrom(() => [
        this.store.select(selectAvailabilitySelectedFaresState),
        this.store.select(selectSeatMaps),
      ]),
      switchMap(([action, fares, seats]) => {
        this.scrollService.scrollToTop(true);
        if (action.redirectToPayment) {
          const isFlextraSelected = (fares?.filter(fare => fare?.details.productClass === CabinProductClass.PREMIUM_PLUS || fare?.details.productClass === CabinProductClass.ECONOMY_PLUS).length || 0)  > 0;
          if (!isFlextraSelected) {
            return of(appNoOp());
          } else {
            if (!seats) {
              return this.seatService
                .getSeatMaps()
                .pipe(
                  switchMap(({data: seatMaps}) => {
                      return of(autoAssignSeats({seatMaps}))
                    }
                  ),
                );
            } else {
              return of(autoAssignSeats({seatMaps: seats}));
            }
          }
        } else {
          return of(appNoOp());
        }
      }),
    );
  });

  // needed for feat-002191-skip-to-payment-button only
  autoAssignSeats: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(autoAssignSeats),
      concatLatestFrom(() => [
        this.store.select(selectAvailabilitySelectedFaresState),
        this.store.select(selectPassengers),
        this.store.select(selectSegmentsWithJourneyKeys),
      ]),
      switchMap(([action, fares, passengers, segments]) => {
        const numberOfPassengers = passengers ? Object.keys(passengers).length : 0;

        let seatsToSelect: (UnitInformation | undefined)[] = [];

        if (numberOfPassengers === 1) {
          for (let i = 0; i < (segments?.length || 1); i++) {
            if (isFlextra(fares?.[i])) {
              let cabinClass: string = ECONOMY_CABIN_DESIGNATOR;
              if (fares?.[i]?.details.productClass === CabinProductClass.PREMIUM_PLUS) {
                cabinClass = PREMIUM_CABIN_DESIGNATOR;
              }
              const availableSeats = action.seatMaps?.[i]?.seatMap?.decks?.['1']?.compartments?.[cabinClass];
              if (!availableSeats?.units) {
                return of(appNoOp());
              }
              const goodSeat = findGoodSeat(availableSeats.units, cabinClass);
              if (goodSeat) {
                seatsToSelect.push(goodSeat);
              }
            }
          }
        } else {
          for (let i = 0; i < (segments?.length || 1); i++) {
            if (isFlextra(fares?.[i])) {
              let cabinClass: string = ECONOMY_CABIN_DESIGNATOR;
              if (fares?.[i]?.details.productClass === CabinProductClass.PREMIUM_PLUS) {
                cabinClass = PREMIUM_CABIN_DESIGNATOR;
              }
              const availableSeats = action.seatMaps?.[i]?.seatMap?.decks?.['1']?.compartments?.[cabinClass];
              if (!availableSeats?.units) {
                return of(appNoOp());
              }
              seatsToSelect = [...seatsToSelect, ...findGoodSeatsForXPassengers(availableSeats.units, cabinClass, numberOfPassengers)]
            }
          }
        }

        const payload: N0PassengerSeatRequest[] = [];
        let seatToSelect = 0;

        for (let i = 0; i < (segments?.length || 1); i++) {
          for (let j = 0; j < (numberOfPassengers || 1); j++) {
            payload.push({
              journeyKey: segments?.[i]?.journeyKey || '',
              passengerKey: Object.values(passengers ?? {})?.[j]?.passengerKey || '',
              unitKey: seatsToSelect[seatToSelect]?.unitKey || ''
            })
            seatToSelect++;
          }
        }

        return of(updatePassengerSeats({
              updates: payload,
              redirectToPayment: true,
              ssrs: []
            },
          )
        )
      }
    ))
  });

  getPassengerUpdates(
    passengers: {
      [key: string]: Passenger;
    },
    phoneCodes: CountryPhoneCode[],
    forms: {
      [key: string]: PassengerForm;
    },
    culture?: NullableString,
  ): PassengerUpdateRequest[] {
    return Object.keys(passengers)
      .filter((paxKey) => !!forms[paxKey])
      .map((passengerKey) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const passengerFormData = forms[passengerKey]!;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const passenger = passengers[passengerKey]!;
        return this.passengerService.transformFormDataToPassengerUpdateRequest(
          passenger,
          phoneCodes,
          passengerFormData,
          culture,
        );
      });
  }

  getTripDetailsPassengerUpdates(
    passengers: {
      [key: string]: Passenger;
    },
    forms: {
      [key: string]: TripDetailsPassengerForm;
    },
    phoneCodes: CountryPhoneCode[],
    culture?: NullableString,
  ): PassengerUpdateRequest[] {
    return Object.keys(passengers)
      .filter((paxKey) => !!forms[paxKey])
      .map((passengerKey) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const passengerFormData = forms[passengerKey]!;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const passenger = passengers[passengerKey]!;
        return this.passengerService.transformTripDetailsFormDataToPassengerUpdateRequest(
          passenger,
          passengerFormData,
          phoneCodes,
          culture,
        );
      });
  }

  getPassengerAssistanceUpdates(
    passengers: {
      [key: string]: Passenger;
    },
    forms: {
      [key: string]: PassengerForm | TripDetailsPassengerForm;
    },
  ): PassengerAssistanceUpdateRequest[] {
    return Object.keys(passengers)
      .filter((paxKey) => !!forms[paxKey])
      .map((passengerKey) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const passengerFormData = forms[passengerKey]!;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const passenger = passengers[passengerKey]!;
        return this.passengerService.transformFormDataToPassengerAssistanceUpdateRequest(
          passenger,
          passengerFormData,
        );
      });
  }
}
